import axios from 'axios';
import strapiService from './strapi.js';

// Instancia de axios para la API de Strapi
const strapiApi = axios.create({
  baseURL: 'https://staging-strapi.orsai.org/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  validateStatus: function (status) {
    return status >= 200 && status < 300; // default
  },
  transformResponse: [function (data) {
    // Si la respuesta es un string, intentar parsearlo como JSON
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        // Si no se puede parsear como JSON, devolver el string tal como está
        return data;
      }
    }
    return data;
  }]
});

/**
 * Obtiene cuentos con versiones desde la API de Strapi
 * @param {number} page - Número de página
 * @param {string} version - Versión de los cuentos (ej: 'V4')
 * @param {Object} filters - Filtros a aplicar
 * @param {number} pageSize - Tamaño de página (por defecto 24)
 * @returns {Promise} - Promise con los datos de los cuentos
 */
export const getCuentosWithVersions = async (page = 1, version = 'V4', filters = {}, pageSize = 24) => {
  try {
    // Extraer pageSize de los filtros si existe, sino usar el proporcionado
    const { pageSize: filterPageSize, ...otherFilters } = filters;
    // Asegurar que pageSize sea un número
    const finalPageSize = parseInt(filterPageSize || pageSize);

    const params = {
      version,
      'pagination[page]': page,
      'pagination[pageSize]': finalPageSize,
      sort: 'updatedAt:desc',
      ...otherFilters
    };

    // Asegurar que pageSize se envíe explícitamente como parámetro de query
    params.pageSize = finalPageSize;

    console.log('🔍 Parámetros enviados al servicio:', {
      page,
      pageSize,
      finalPageSize,
      version,
      filters,
      params,
      url: `${strapiApi.defaults.baseURL}/cuentos/with-versions?${new URLSearchParams(params).toString()}`
    });

    const response = await strapiApi.get('/cuentos/with-versions', { params });
    const data = response.data;

    console.log(`📚 Cuentos obtenidos - Página ${page}, PageSize: ${finalPageSize}, Total recibidos: ${data?.data?.length || 0}:`, {
      totalEnPagina: data?.data?.length || 0,
      paginacion: data?.meta?.pagination,
      filtrosEnviados: filters,
      pageSizeEnviado: finalPageSize,
      pageSizeDevuelto: data?.meta?.pagination?.pageSize,
      coinciden: finalPageSize === data?.meta?.pagination?.pageSize
    });

    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos:', error);
    throw error;
  }
};

/**
 * Obtiene las opciones de filtros disponibles
 * @param {string} version - Versión de los cuentos
 * @returns {Promise} - Promise con las opciones de filtros
 */
export const getCuentosFilterOptions = async (version = 'V4') => {
  try {
    const response = await strapiApi.get('/alumnos/filters', { params: { version } });
    const data = response.data;
    
    console.log('🔍 Opciones de filtros obtenidas:', data);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener opciones de filtros:', error);
    throw error;
  }
};

/**
 * Transforma los datos de la API al formato esperado por el componente
 * @param {Array} cuentosData - Datos de cuentos de la API
 * @returns {Array} - Array de cuentos transformados
 */
export const transformCuentosData = (cuentosData) => {
  return cuentosData.map(cuento => ({
    id: cuento.id,
    title: cuento.titulo || 'Sin título',
    author: cuento.alumno ? `${cuento.alumno.nombre} ${cuento.alumno.apellido}` : 'Anónimo',
    description: cuento.contenido?.resumen || cuento.resumen || 'Sin descripción disponible',
    image: cuento.cover || '/src/assets/images/Sonoro/anecdota.png',
    audio: cuento.audio_v2,
    genre: cuento.taller?.genero || 'Anécdota',
    tags: (cuento.tags || []).map(tag => tag.charAt(0).toUpperCase() + tag.slice(1)),
    documentId: cuento.documentId,
    idUsuario: cuento.idUsuario, // ID del usuario autor directamente del cuento
    createdAt: cuento.createdAt,
    publishedAt: cuento.publishedAt,
    // Datos del alumno para filtros
    alumno: cuento.alumno ? {
      id: cuento.alumno.id,
      idUsuario: cuento.alumno.idUsuario, // Agregar idUsuario al objeto alumno
      nombre: cuento.alumno.nombre,
      apellido: cuento.alumno.apellido,
      pais: cuento.alumno.pais,
      paisNacimiento: cuento.alumno.paisNacimiento,
      genero: cuento.alumno.genero,
      fechaNacimiento: cuento.alumno.fechaNacimiento,
      estudios: cuento.alumno.estudios,
      profesion: cuento.alumno.profesion,
      estadoCivil: cuento.alumno.estadoCivil,
      hijos: cuento.alumno.hijos,
      signo: cuento.alumno.signo,
      grupoEtario: cuento.alumno.grupoEtario
    } : null
  }));
};

/**
 * Obtiene un cuento por documentId desde la API de Strapi
 * @param {string} documentId - El documentId del cuento
 * @returns {Promise} - Promise con los datos del cuento
 */
export const getCuentoByDocumentId = async (documentId) => {
  const endpoints = [
    `/cuentos/document/${documentId}`,
    `/cuentos?filters[documentId]=${documentId}&populate=*`,
    `/cuentos?filters[documentId][$eq]=${documentId}&populate=*`
  ];

  const errors = [];

  for (let i = 0; i < endpoints.length; i++) {
    const endpoint = endpoints[i];
    try {
      console.log(`🔍 Intentando endpoint ${i + 1}/${endpoints.length}: ${endpoint}`);
      
      const response = await strapiApi.get(endpoint);
      let data = response.data;
      
      // Verificar que la respuesta sea válida (no HTML)
      if (typeof data === 'string') {
        const trimmedData = data.trim();
        if (trimmedData.startsWith('<!DOCTYPE html>') || trimmedData.startsWith('<html>')) {
          throw new Error('API returned HTML instead of JSON');
        }
        
        // Intentar parsear como JSON si es string
        try {
          data = JSON.parse(trimmedData);
        } catch (parseError) {
          throw new Error('Invalid JSON response from API');
        }
      }
      
      // Para endpoints alternativos, verificar la estructura de datos
      if (i > 0 && data && data.data && Array.isArray(data.data)) {
        if (data.data.length > 0) {
          console.log(`✅ Cuento encontrado con endpoint alternativo ${i + 1}`);
          return data.data[0];
        } else {
          throw new Error('No cuento found in array response');
        }
      }
      
      if (!data) {
        throw new Error('Empty response from API');
      }
      
      console.log(`✅ Cuento encontrado con endpoint ${i + 1}`);
      return data;
      
    } catch (error) {
      const errorMsg = `Endpoint ${i + 1} (${endpoint}): ${error.message}`;
      console.error(`❌ ${errorMsg}`);
      errors.push(errorMsg);
      
      // Si es el último endpoint, re-throw el error con más contexto
      if (i === endpoints.length - 1) {
        const errorMessage = `No se pudo obtener el cuento con documentId: ${documentId}. 
          Todos los endpoints fallaron:
          ${errors.map(err => `- ${err}`).join('\n')}`;
        throw new Error(errorMessage);
      }
      
      // Continuar con el siguiente endpoint
      continue;
    }
  }
};

/**
 * Obtiene un cuento por su ID numérico
 * @param {number} id - El ID numérico del cuento
 * @returns {Promise} - Promise con los datos del cuento
 */
export const getCuentoById = async (id) => {
  try {
    // Usar el método específico que incluye la relación con alumno
    const response = await strapiService.getCuentoWithAlumno(id);
    
    // Obtener el primer resultado de data si existe
    const cuento = response?.data?.[0];

    if (!cuento) {
      throw new Error(`No se encontró cuento con ID: ${id}`);
    }

    console.log('🔍 Debug getCuentoById - cuento recibido:', cuento);
    console.log('🔍 Debug getCuentoById - alumno en cuento:', cuento?.alumno);

    return cuento;
  } catch (error) {
    console.error('❌ Error al obtener cuento por ID:', error);
    throw error;
  }
};

/**
 * Obtiene las valoraciones de un cuento específico
 * @param {string} documentId - El documentId del cuento
 * @returns {Promise} - Promise con las valoraciones del cuento
 */
export const getCuentoValoraciones = async (documentId) => {
  try {
    const response = await strapiApi.get(`/valoraciones/cuento/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error al obtener valoraciones del cuento:', error);
    throw error;
  }
};

/**
 * Envía una valoración para un cuento
 * @param {string} documentId - El documentId del cuento
 * @param {number} rating - Valoración (1-5)
 * @param {number} userId - ID del usuario
 * @param {string} userEmail - Email del usuario
 * @returns {Promise} - Promise con la respuesta del servidor
 */
export const enviarValoracion = async (documentId, rating, userId, userEmail) => {
  try {
    // Validar que se proporcionen los datos requeridos
    if (!userId || !userEmail) {
      throw new Error('Se requieren userId y userEmail para valorar cuentos');
    }

    // Validar que el rating esté en el rango correcto
    if (rating < 1 || rating > 5) {
      throw new Error('La valoración debe estar entre 1 y 5');
    }

    const response = await strapiApi.post(`/valoraciones/cuento/${documentId}`, {
      rating,
      userId,
      userEmail
    });

    return response.data;
  } catch (error) {
    console.error('❌ Error al enviar valoración:', error);
    throw error;
  }
};

/**
 * Verifica si un usuario ya valoró un cuento específico
 * @param {string} documentId - El documentId del cuento
 * @param {string} userEmail - Email del usuario
 * @returns {Promise} - Promise con la valoración existente o null
 */
export const verificarValoracionUsuario = async (documentId, userEmail) => {
  try {
    const response = await strapiApi.get(`/valoraciones/cuento/${documentId}/usuario`, { 
      params: { userEmail } 
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error al verificar valoración del usuario:', error);
    return null;
  }
};

/**
 * Obtiene estadísticas de valoraciones de un cuento
 * @param {string} documentId - El documentId del cuento
 * @returns {Promise} - Promise con las estadísticas de valoraciones
 */
export const getEstadisticasValoraciones = async (documentId) => {
  try {
    const response = await strapiApi.get(`/valoraciones/cuento/${documentId}/estadisticas`);
    return response.data;
  } catch (error) {
    console.error('❌ Error al obtener estadísticas de valoraciones:', error);
    throw error;
  }
};

/**
 * Obtiene los cuentos mejor valorados desde el endpoint específico
 * @returns {Promise} - Promise con los datos de los cuentos mejor valorados
 */
export const getCuentosMejorValorados = async () => {
  try {
    console.log('🔍 Obteniendo cuentos mejor valorados...');

    const response = await strapiApi.get('/cuentos/mejor-valorados');
    const data = response.data;

    console.log('📚 Cuentos mejor valorados obtenidos:', {
      totalRecibidos: data?.data?.length || 0,
      datos: data
    });

    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos mejor valorados:', error);
    throw error;
  }
};

/**
 * Obtiene el cuento anterior basado en fecha de publicación
 * @param {string} currentDocumentId - El documentId del cuento actual
 * @returns {Promise} - Promise con el cuento anterior
 */
export const getCuentoAnterior = async (currentDocumentId) => {
  try {
    const response = await strapiApi.get(`/cuentos/adjacent/${currentDocumentId}`, { 
      params: { direction: 'previous' } 
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error al obtener cuento anterior:', error);
    throw error;
  }
};

/**
 * Obtiene el cuento siguiente basado en fecha de publicación
 * @param {string} currentDocumentId - El documentId del cuento actual
 * @returns {Promise} - Promise con el cuento siguiente
 */
export const getCuentoSiguiente = async (currentDocumentId) => {
  try {
    const response = await strapiApi.get(`/cuentos/adjacent/${currentDocumentId}`, { 
      params: { direction: 'next' } 
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error al obtener cuento siguiente:', error);
    throw error;
  }
};

/**
 * Obtiene cuentos adyacentes (anterior y siguiente) basados en fecha de publicación
 * @param {string} currentDocumentId - El documentId del cuento actual
 * @returns {Promise} - Promise con los cuentos adyacentes
 */
export const getCuentosAdyacentes = async (currentDocumentId) => {
  try {
    const response = await strapiApi.get(`/cuentos/adjacent/${currentDocumentId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos adyacentes:', error);
    throw error;
  }
};

/**
 * Obtiene cuentos anterior y siguiente usando el nuevo endpoint
 * @param {string} documentId - El documentId del cuento actual
 * @returns {Promise} - Promise con actual, anterior y siguiente
 */
export const getCuentosAnteriorSiguiente = async (documentId) => {
  try {
    console.log('🔍 Obteniendo cuentos anterior/siguiente para:', documentId);

    const response = await strapiApi.get(`/cuentos/${documentId}/anterior-siguiente`);
    const data = response.data;

    console.log('📚 Cuentos anterior/siguiente obtenidos:', {
      actual: data?.actual?.documentId,
      anterior: data?.anterior?.documentId,
      siguiente: data?.siguiente?.documentId
    });

    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos anterior/siguiente:', error);
    throw error;
  }
}; 